import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiUser, FiMail, FiAlertTriangle } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import {
  checkPasswordStrength,
  isEmailValid,
  doPasswordsMatch,
  validatePasswordRequirements,
} from "../../utils/validationUtils";
import { handleAuthError, getErrorActionHint } from "../../utils/errorUtils";
import {
  AuthLayout,
  AuthInput,
  AuthButton,
  PasswordInput,
  SocialAuthButton,
} from "./components";
import styles from "./Auth.module.css";

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, googleLogin } = useUserContext();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorAction, setErrorAction] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: "",
  });

  // Get the redirect path if any
  const from = location.state?.from || "/";

  // Check for Google auth in progress
  useEffect(() => {
    if (window.location.search.includes("token=")) {
      setIsGoogleLoading(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "password") {
      setPasswordStrength(checkPasswordStrength(value));
    }

    if (name === "confirmPassword" || name === "password") {
      setPasswordMismatch(false);
    }

    if (error) {
      setError("");
      setErrorAction(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEmailValid(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!doPasswordsMatch(formData.password, formData.confirmPassword)) {
      setPasswordMismatch(true);
      return;
    }

    const passwordValidation = validatePasswordRequirements(formData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      };

      await signup(userData);

      navigate("/email-verification", {
        state: {
          email: formData.email,
          from: "/onboarding",
        },
      });
    } catch (err) {
      const processedError = handleAuthError(err);
      setError(processedError.message);

      if (processedError.action) {
        setErrorAction(getErrorActionHint(processedError.action));
      } else {
        setErrorAction(null);
      }

      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = (e) => {
    e.preventDefault();
    setIsGoogleLoading(true);
    googleLogin();
  };

  // Google loading state
  if (isGoogleLoading) {
    return (
      <AuthLayout
        title="Signing up with Google"
        subtitle="Please wait while we create your account..."
      >
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <AuthButton loading fullWidth>
            Connecting...
          </AuthButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create Account"
      subtitle="Join us to get started"
      footer={
        <>
          Already have an account?{" "}
          <Link to="/signin" state={{ from }} className={styles['au-link']}>
            Sign in
          </Link>
        </>
      }
    >
      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <div className={styles['au-alert__content']}>
              <span>{error}</span>
              {errorAction?.link && (
                <Link to={errorAction.link} className={styles['au-alert__action']}>
                  {errorAction.text}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sign Up Form */}
      <form onSubmit={handleSubmit} className={styles['au-form']}>
        <AuthInput
          icon={FiUser}
          label="Full Name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter your full name"
          required
          disabled={isLoading}
        />

        <AuthInput
          icon={FiMail}
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          required
          disabled={isLoading}
        />

        <PasswordInput
          label="Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a password"
          required
          disabled={isLoading}
          showStrength={true}
          strength={passwordStrength}
        />

        <PasswordInput
          label="Confirm Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm your password"
          required
          disabled={isLoading}
          error={passwordMismatch ? "Passwords do not match" : ""}
        />

        <div className={styles['au-form-options']}>
          <label className={styles['au-checkbox']}>
            <input
              type="checkbox"
              className={styles['au-checkbox__input']}
              required
              disabled={isLoading}
            />
            <span className={styles['au-checkbox__label']}>
              I agree to the{" "}
              <Link to="/terms" className={styles['au-link']}>
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className={styles['au-link']}>
                Privacy Policy
              </Link>
            </span>
          </label>
        </div>

        <AuthButton
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </AuthButton>

        <div className={styles['au-divider']}>
          <span className={styles['au-divider__text']}>or</span>
        </div>

        <SocialAuthButton
          provider="google"
          onClick={handleGoogleSignUp}
          loading={isGoogleLoading}
          disabled={isLoading}
        />
      </form>
    </AuthLayout>
  );
};

export default SignUp;
